import { ASSESSMENT_SLUG } from "core/consts";
import { hasMobileSolution } from "core/model/patients";
import { formatUnixDate } from "core/model/utils/dates";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { Auction, GetOntologyType } from "core/types";
import { solutionSortOrder } from "dsl/ecosystems/PatientCreatePage/patientCreateFormUtils";
import { getSelectedOntologies } from "dsl/ecosystems/PatientProfile";
import { PatientInfoSlugContext } from "dsl/organisms/PatientProfileDetailsShared";
import {
  BodyWrapper,
  LocationContent,
  PatientProfileInfoCard,
  StringValue,
} from "dsl/organisms/PatientProfileDetailsShared/shared";
import {
  Locale as LocaleString,
  useLocale,
  useTranslations,
} from "translations";
import Translations from "translations/types";
import {
  getFrequencyLabel,
  getRequiredCareTypeLabel,
  getVisitTimeLabel,
} from "./utils";

function getStartDateLabel(
  auction: Auction | null | undefined,
  locale: LocaleString,
  translations: Translations,
): string {
  if (!auction?.start_date) return translations.general.unknown;

  const flexible = auction?.start_date_flexible
    ? ` ${translations.careproviderApp.recareAdmissionsForm.desiredCare.dateFlexible}`
    : "";

  const startDate = formatUnixDate(auction.start_date, locale);

  return [startDate, flexible].truthy().join(", ");
}

export function StaticCareInformation({
  auction,
  getOntology,
  locale,
  translations,
}: {
  getOntology: GetOntologyType;
  translations: Translations;
  auction: Auction;
  locale: LocaleString;
}) {
  const solutionTranslations =
    getSelectedOntologies(
      auction.solutions,
      ONTOLOGIES.SOLUTION,
      getOntology,
      solutionSortOrder,
    ) ?? translations.general.unknown;

  const desiredCareTypeLabel = getRequiredCareTypeLabel({
    auction,
    getOntology,
    translations,
  });

  return (
    <>
      <BodyWrapper>
        <b className="-mr-3 font-bold">{`${translations.careproviderApp.recareAdmissionsForm.desiredCare.titleStartDate}${translations.general.colon}`}</b>
        <StringValue
          testId="admission_start_date"
          value={getStartDateLabel(auction, locale, translations)}
        />
      </BodyWrapper>
      <BodyWrapper>
        <b className="-mr-3 font-bold">{`${translations.careproviderApp.recareAdmissionsForm.desiredCare.titleDesiredTypeOfCare}${translations.general.colon}`}</b>
        {desiredCareTypeLabel ? (
          <StringValue
            testId="admission_desired_care_type"
            value={desiredCareTypeLabel}
          />
        ) : (
          <StringValue
            testId="admission_solutions"
            value={solutionTranslations}
          />
        )}
      </BodyWrapper>
    </>
  );
}

export function MobileCareInformation({
  auction,
  getOntology,
  locale,
  translations,
}: {
  getOntology: GetOntologyType;
  translations: Translations;
  auction: Auction;
  locale: LocaleString;
}) {
  return (
    <>
      <LocationContent
        location={auction?.profile?.search_location}
        prefixText={
          translations.careproviderApp.recareAdmissionsProfile.patientAddress
        }
        testId="search_location"
      />
      <StaticCareInformation
        auction={auction}
        getOntology={getOntology}
        locale={locale}
        translations={translations}
      />
      <BodyWrapper>
        <b className="-mr-3 font-bold">{`${translations.careproviderApp.recareAdmissionsProfile.frequencyOfCare}${translations.general.colon}`}</b>
        <StringValue
          testId="care_frequency"
          value={getFrequencyLabel(
            getOntology,
            translations,
            auction?.admissions?.profile?.care_frequency,
            auction?.admissions?.profile?.care_frequency_other?.decrypted,
          )}
        />
      </BodyWrapper>
      <BodyWrapper>
        <b className="-mr-3 font-bold">{`${translations.careproviderApp.recareAdmissionsProfile.preferredTime}${translations.general.colon}`}</b>
        <StringValue
          testId="visit_time"
          value={getVisitTimeLabel(
            getOntology,
            translations,
            auction.admissions?.profile?.visit_times,
            auction.admissions?.profile?.visit_times_other?.decrypted,
          )}
        />
      </BodyWrapper>
    </>
  );
}

export function AdmissionsCareCard({ auction }: { auction: Auction }) {
  const getOntology = useGetOntology();
  const translations = useTranslations();
  const locale = useLocale();

  return (
    <PatientInfoSlugContext.Provider
      value={{
        assessmentSlug: ASSESSMENT_SLUG.INFO,
        auctionId: auction.id,
        patientId: auction.patient.id,
      }}
    >
      <PatientProfileInfoCard
        testId="desired_care_information_category_container"
        title={
          translations.careproviderApp.recareAdmissionsForm.desiredCare.title
        }
      >
        {hasMobileSolution(auction.solutions) ? (
          <MobileCareInformation
            auction={auction}
            getOntology={getOntology}
            locale={locale}
            translations={translations}
          />
        ) : (
          <StaticCareInformation
            auction={auction}
            getOntology={getOntology}
            locale={locale}
            translations={translations}
          />
        )}
      </PatientProfileInfoCard>
    </PatientInfoSlugContext.Provider>
  );
}
